"use client";

import { useEffect, useState, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import { Loader2, Lock, ArrowRight } from "lucide-react";

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

// 글로벌 인증 상태 캐시 (더 긴 캐시 시간과 상태 관리)
const authCache = {
  isAuthenticated: null as boolean | null,
  timestamp: 0,
  ttl: 60000, // 1분 캐시 (기존 5초 → 60초)
  pendingRequest: null as Promise<boolean> | null, // 중복 요청 방지
};

// 전역에서 캐시에 접근할 수 있도록 window 객체에 노출
if (typeof window !== "undefined") {
  // @ts-ignore
  window.authCache = authCache;
}

export function AuthGuard({
  children,
  fallback,
  redirectTo = "/login",
}: AuthGuardProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [countdown, setCountdown] = useState(2);
  const router = useRouter();
  const pathname = usePathname();
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    // 인증되지 않은 경우 카운트다운 시작
    if (isAuthenticated === false) {
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            if (countdownRef.current) {
              clearInterval(countdownRef.current);
            }
            const returnUrl = encodeURIComponent(
              pathname + window.location.search
            );
            router.push(`${redirectTo}?returnUrl=${returnUrl}`);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [isAuthenticated, pathname, redirectTo, router]);

  const checkAuth = async () => {
    try {
      // 캐시 확인
      const now = Date.now();
      if (
        authCache.isAuthenticated !== null &&
        now - authCache.timestamp < authCache.ttl
      ) {
        console.log("🔥 인증 상태 캐시 사용 - API 호출 없음");
        setIsAuthenticated(authCache.isAuthenticated);
        setIsLoading(false);
        return;
      }

      // 이미 진행 중인 요청이 있으면 대기
      if (authCache.pendingRequest) {
        console.log("⏳ 기존 인증 요청 대기 중 - 중복 호출 방지");
        const result = await authCache.pendingRequest;
        setIsAuthenticated(result);
        setIsLoading(false);
        return;
      }

      // 새로운 인증 요청 시작
      console.log("🚀 새로운 인증 API 호출 시작");
      authCache.pendingRequest = performAuthCheck();

      const result = await authCache.pendingRequest;
      setIsAuthenticated(result);
    } catch (error) {
      console.error("인증 확인 오류:", error);
      // 네트워크 오류 시에도 캐시된 값이 있으면 사용
      if (authCache.isAuthenticated !== null) {
        setIsAuthenticated(authCache.isAuthenticated);
      } else {
        setIsAuthenticated(false);
      }
    } finally {
      authCache.pendingRequest = null; // 요청 완료 후 초기화
      setIsLoading(false);
    }
  };

  const performAuthCheck = async (): Promise<boolean> => {
    // AbortController로 요청 타임아웃 설정
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3초 타임아웃

    try {
      const response = await fetch("/api/auth/me", {
        method: "GET",
        credentials: "include",
        signal: controller.signal,
        headers: {
          "Cache-Control": "no-cache",
        },
      });

      clearTimeout(timeoutId);
      const now = Date.now();

      if (response.ok) {
        authCache.isAuthenticated = true;
        authCache.timestamp = now;
        console.log("✅ 인증 성공 - 캐시 업데이트");
        return true;
      } else {
        authCache.isAuthenticated = false;
        authCache.timestamp = now;
        console.log("❌ 인증 실패 - 캐시 업데이트");
        return false;
      }
    } finally {
      clearTimeout(timeoutId);
    }
  };

  // 인증되지 않음 (로딩이 끝난 후 확정된 경우)
  if (!isLoading && !isAuthenticated) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="h-8 w-8 text-orange-600 dark:text-orange-400" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            로그인이 필요합니다
          </h2>

          <p className="text-gray-600 dark:text-gray-400 mb-6">
            이 페이지에 접근하려면 먼저 로그인해주세요.
            <br />
            로그인 후 원래 페이지로 자동으로 이동됩니다.
          </p>

          <div className="space-y-3">
            <div className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 mb-4">
              <Loader2 className="animate-spin h-4 w-4 mr-2" />
              <span>{countdown}</span>초 후 자동으로 로그인 페이지로 이동합니다
            </div>

            <button
              onClick={() => {
                if (countdownRef.current) {
                  clearInterval(countdownRef.current);
                }
                const returnUrl = encodeURIComponent(
                  pathname + window.location.search
                );
                router.push(`${redirectTo}?returnUrl=${returnUrl}`);
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center justify-center gap-2"
            >
              지금 로그인하기
              <ArrowRight className="h-4 w-4" />
            </button>

            <button
              onClick={() => {
                if (countdownRef.current) {
                  clearInterval(countdownRef.current);
                }
                router.push("/");
              }}
              className="w-full bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 font-medium py-2 px-4 rounded-md transition-colors"
            >
              홈으로 가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 인증됨 또는 인증 확인 중 (인증 확인 중에는 콘텐츠를 유지하여 웹소켓 끊김 방지)
  return (
    <div className="relative min-h-screen">
      {/* 실제 콘텐츠 */}
      {children}

      {/* 로딩 오버레이 (인증 확인 중) */}
      {isLoading && (
        <div className="fixed inset-0 bg-background/60 backdrop-blur-[2px] z-[9999] flex items-center justify-center transition-all duration-300">
          <div className="bg-card/90 p-6 rounded-xl shadow-2xl border flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-4"></div>
            <p className="text-sm font-medium text-muted-foreground animate-pulse">
              인증 상태 확인 중...
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// 카운트다운 스크립트 (클라이언트 사이드)
if (typeof window !== "undefined") {
  let countdown = 2;
  const countdownInterval = setInterval(() => {
    countdown--;
    const countdownElement = document.getElementById("countdown");
    if (countdownElement) {
      countdownElement.textContent = countdown.toString();
    }
    if (countdown <= 0) {
      clearInterval(countdownInterval);
    }
  }, 1000);
}
